/**
 * Master Packages — view + edit grants.
 * Presets (Básico/Pro/Premium/Personalizado) are editable but not deletable.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Loader2, Save, Bot, LayoutGrid, DollarSign, CheckCircle2 } from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterPackages() {
  const [packages, setPackages] = useState([]);
  const [catalog, setCatalog] = useState({ agents: [], modules: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/master/packages`),
        axios.get(`${API}/master/catalog`),
      ]);
      setPackages(p.data.packages || []);
      setCatalog(c.data || { agents: [], modules: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <MasterLayout title="Packages">
      <h2 className="text-2xl font-extrabold tracking-tight mb-6">Service Packages</h2>
      {loading ? (
        <div className="flex items-center gap-2 text-white/55 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} catalog={catalog} onSaved={load} />
          ))}
        </div>
      )}
    </MasterLayout>
  );
}

function PackageCard({ pkg, catalog, onSaved }) {
  const [agents, setAgents] = useState(pkg.agents || []);
  const [modules, setModules] = useState(pkg.modules || []);
  const [leadsLimit, setLeadsLimit] = useState(pkg.leads_per_month || 0);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(agents) !== JSON.stringify(pkg.agents || []) ||
                JSON.stringify(modules) !== JSON.stringify(pkg.modules || []) ||
                leadsLimit !== (pkg.leads_per_month || 0);

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/master/packages/${pkg.id}`, {
        name: pkg.name, color: pkg.color,
        agents, modules, leads_per_month: leadsLimit, features: pkg.features || {},
      });
      toast.success(`${pkg.name} saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`master-pkg-card-${pkg.slug}`}
      className="rounded-xl border p-5 space-y-4"
      style={{ borderColor: `${pkg.color}55`, background: `${pkg.color}08` }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5" style={{ color: pkg.color }} />
          <h3 className="text-white font-extrabold text-lg">{pkg.name}</h3>
          {pkg.is_preset && (
            <span className="text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
              style={{ background: `${pkg.color}22`, color: pkg.color }}>preset</span>
          )}
        </div>
        <button data-testid={`master-pkg-save-${pkg.slug}`} onClick={save} disabled={!dirty || busy}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold disabled:opacity-40 text-[11.5px]">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-2 flex items-center gap-1">
          <Bot className="w-3 h-3" /> Agents ({agents.length}/{catalog.agents.length})
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {catalog.agents.map((a) => {
            const on = agents.includes(a.id);
            return (
              <button key={a.id} type="button"
                data-testid={`master-pkg-${pkg.slug}-agent-${a.id}`}
                onClick={() => toggle(agents, setAgents, a.id)}
                title={a.description}
                className={`text-left px-2 py-1.5 rounded text-[11px] border transition-all ${
                  on ? "border-[#C9A24E] bg-[#C9A24E]/15 text-white" : "border-white/10 text-white/55 hover:border-white/30"
                }`}>
                {on && "✓ "}{a.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-2 flex items-center gap-1">
          <LayoutGrid className="w-3 h-3" /> Modules ({modules.length}/{catalog.modules.length})
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {catalog.modules.map((m) => {
            const on = modules.includes(m.id);
            return (
              <button key={m.id} type="button"
                data-testid={`master-pkg-${pkg.slug}-module-${m.id}`}
                onClick={() => toggle(modules, setModules, m.id)}
                className={`text-left px-2 py-1.5 rounded text-[11px] border transition-all ${
                  on ? "border-[#C9A24E] bg-[#C9A24E]/15 text-white" : "border-white/10 text-white/55 hover:border-white/30"
                }`}>
                {on && "✓ "}{m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Leads / month (0 = unlimited)</div>
        <input
          type="number" min={0} value={leadsLimit}
          onChange={(e) => setLeadsLimit(Number(e.target.value || 0))}
          data-testid={`master-pkg-${pkg.slug}-leads`}
          className="w-32 h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
          style={{ borderColor: "rgba(201,162,78,0.2)" }} />
      </div>

      <StripeSyncRow pkg={pkg} onSynced={onSaved} />
    </div>
  );
}

function StripeSyncRow({ pkg, onSynced }) {
  const [price, setPrice] = useState(pkg.monthly_price_usd || 0);
  const [busy, setBusy] = useState(false);

  const sync = async () => {
    if (!price || price <= 0) {
      toast.error("Enter a valid monthly price (USD)");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/master/packages/${pkg.id}/sync-stripe`,
        { monthly_price_usd: Number(price) });
      toast.success(`${pkg.name} synced to Stripe`);
      onSynced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe sync failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-3" style={{ borderColor: "rgba(201,162,78,0.15)" }}>
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-2 flex items-center gap-1">
        <DollarSign className="w-3 h-3" /> Stripe Subscription Price (USD/month)
        {pkg.stripe_price_id && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-400 font-bold text-[10px]">
            <CheckCircle2 className="w-3 h-3" /> Synced
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number" min={0} step="0.01" value={price}
          onChange={(e) => setPrice(e.target.value)}
          data-testid={`master-pkg-${pkg.slug}-stripe-price`}
          className="w-32 h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
          style={{ borderColor: "rgba(201,162,78,0.2)" }} />
        <button onClick={sync} disabled={busy}
          data-testid={`master-pkg-${pkg.slug}-stripe-sync`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#635BFF] text-white font-bold disabled:opacity-40 text-[11.5px] hover:bg-[#5048DC]">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
          {pkg.stripe_price_id ? "Re-sync" : "Sync to Stripe"}
        </button>
      </div>
      {pkg.stripe_price_id && (
        <div className="text-[10px] text-white/40 mt-1 font-mono">price: {pkg.stripe_price_id}</div>
      )}
    </div>
  );
}
